'use client';

import { consoleLog, mapApiUserToFrontend, quickLog } from '@/utils';
import { FEATURE_FLAGS, ROUTES } from '@/constants';
import { trackAuthenticationFailure } from '@/lib/authentication-telemetry';
import { loadCurrentUser } from '@/services';
import { MsalProvider } from '@azure/msal-react';
import { useGlobalStore } from '@/store/global-store';
import { useRouter, usePathname } from 'next/navigation';
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { ApiUser, IUser } from '@/types';
import {
  getMsalInstance,
  initializeMsal,
  getActiveAccount,
  isAccountSessionValid,
  mapAzureUserToAppUser,
  fetchGraphUserPhoto,
  signOut as msalSignOut,
} from '@/lib/msal';

// ================================
// Auth Context Contracts
// ================================
// This file defines both:
// 1) Context primitives (`AuthContext`, `useAuth`) used by consumers.
// 2) Runtime auth orchestration (`AuthProvider`) responsible for bootstrapping
//    identity, persisting session data, and route protection.

/**
 * Authentication Context Type
 */
interface AuthContextType {
  user: IUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: IUser, redirectTo?: string) => void;
  logout: () => void;
}

/**
 * Key used to store post-login redirect target in sessionStorage during auth flow.
 * This allows the app to remember where to send the user after they log in, especially
 * if they were initially trying to access a protected route.
 */
export const POST_LOGIN_REDIRECT_KEY = 'post-login-redirect';

/**
 * Authentication Context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Custom hook to access authentication context.
 * @returns AuthContextType - The authentication context value
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Props for AuthProvider component
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Handles user authentication state, login, logout, and route protection.
 * Wraps children components with AuthContext and MSAL provider.
 *
 * @param children - Child components to wrap
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // ================================
  // Local Auth State
  // ================================
  // `user`: canonical authenticated user model for the current browser session.
  // `isLoading`: guard flag used to delay route protection until bootstrap finishes.
  // User state
  const [user, setUser] = useState<IUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ================================
  // Framework + Store Dependencies
  // ================================
  // Router/pathname power redirects and route protection decisions.
  // Global store integration ensures auth user is mirrored into shared app state.
  // Router and global store
  const router = useRouter();
  const pathname = usePathname();
  const { addNotification, setCurrentUser } = useGlobalStore();

  // ================================
  // One-Time Runtime Guards
  // ================================
  // - `msalInstance` is created once and passed to MsalProvider.
  // - `isInitializing` prevents overlapping async bootstrap calls.
  // - `hasBootstrappedAuth` ensures the bootstrap effect runs only once per app load.
  // Refs and instances
  const [msalInstance] = useState(() => getMsalInstance()); // Create instance once
  const isInitializing = useRef(false); // Prevent concurrent initialization
  const hasBootstrappedAuth = useRef(false); // Prevent re-running auth bootstrap on route changes

  // ================================
  // Auth Bootstrap Lifecycle
  // ================================
  // This effect attempts to resolve the authenticated user from strongest to
  // weakest signal:
  // 1) Fresh MSAL redirect result.
  // 2) Existing active MSAL account.
  // 3) Legacy localStorage fallback.
  //
  // Route changes should not repeatedly trigger costly bootstrap calls, so this
  // effect uses `hasBootstrappedAuth` as a one-time gate.
  useEffect(() => {
    // Skip auth check on callback page - let callback page handle it
    if (pathname === '/auth/callback' || pathname.startsWith('/auth/callback')) {
      hasBootstrappedAuth.current = true;
      consoleLog.info('On callback page, skipping AuthProvider auth check');
      setIsLoading(false);
      return;
    }

    // Auth bootstrap should run once per app load. The callback route delegates
    // that work above and marks this gate complete so a subsequent route change
    // cannot process the same callback again.
    if (hasBootstrappedAuth.current) {
      return;
    }
    hasBootstrappedAuth.current = true;

    /**
     * Check authentication status and initialize user state.
     * Handles MSAL authentication flow, developer bypass, and localStorage fallback.
     * Runs once on component mount.
     */
    const checkAuth = async () => {
      // Prevent concurrent initialization calls (within same execution)
      if (isInitializing.current) {
        consoleLog.info('Auth check already in progress, skipping');
        return;
      }

      isInitializing.current = true;

      try {
        consoleLog.log('🔐 Starting authentication check...');

        // Defensive parser for user data persisted in localStorage.
        // If stale or malformed JSON exists, we return null instead of throwing
        // and breaking bootstrap.
        const parseStoredUser = (): IUser | null => {
          try {
            const raw = localStorage.getItem('user');
            return raw ? (JSON.parse(raw) as IUser) : null;
          } catch {
            return null;
          }
        };

        // Centralized setter for authenticated user side effects so all success
        // branches stay consistent (component state + global store + storage).
        const setAuthenticatedUser = (nextUser: IUser) => {
          setUser(nextUser);
          setCurrentUser(nextUser);
          localStorage.setItem('user', JSON.stringify(nextUser));
          localStorage.setItem('authenticated', 'true');
        };

        // True when the backend definitively rejected this identity, as opposed
        // to a transient network/server issue that should still allow a
        // resilient fallback. Deliberately excludes 403: that means the identity
        // is valid but lacks permission, which re-authenticating cannot fix.
        const isUnauthorizedError = (error: unknown): boolean => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          return status === 401;
        };

        // Clears locally-cached identity and forces a clean re-authentication
        // for a cached MSAL account that turned out to be invalid (dead
        // session) or was explicitly rejected by the backend (401).
        // Routes through `/logout` (NOT directly to the login page) because
        // that endpoint responds with `Clear-Site-Data` (src/app/logout/
        // route.ts), which wipes the MSAL token/account cache in localStorage.
        // Redirecting straight to the login page left a stale/invalid MSAL
        // account in place, so the next page load kept rediscovering it,
        // silently trusting it again, and repeating the same failure -
        // producing an infinite login redirect loop.
        //
        // Guarded to no-op when already on an `/auth` route: that's where an
        // unauthenticated user is SUPPOSED to land, so forcing another hard
        // reload there would just reload the login page forever instead of
        // letting it render normally (this caused a *different* infinite
        // loop - reloading `/auth/login` through `/logout` and back - the
        // first time this guard was missing).
        const clearSessionAndRedirectToLogin = (reason: string) => {
          consoleLog.log(reason);
          localStorage.removeItem('user');
          localStorage.removeItem('authenticated');

          if (pathname.startsWith('/auth')) {
            return;
          }

          window.location.assign(ROUTES.LOGOUT);
        };

        // Resolves the richest available user model for the app:
        // - Prefer backend current-user profile for app-specific fields.
        // - Fall back to cached user for resilience during transient API failures.
        // - Finally fall back to MSAL-mapped identity so auth still succeeds.
        const resolveCurrentUser = async (appUser: IUser, photoUrl: string | null) => {
          const storedUser = parseStoredUser();

          try {
            const apiUser = await loadCurrentUser(appUser.username);
            const combinedUser: ApiUser = { ...apiUser, imageUrl: photoUrl };
            return mapApiUserToFrontend({ apiUser: combinedUser });
          } catch (error) {
            // A definitive 401 means the backend has rejected this identity
            // outright. Falling back to cached/MSAL data below would only mask
            // that and let checkAuth mark the user "authenticated" with data
            // that can never actually be used against the API.
            // Rethrow so the caller treats this the same as an invalid
            // session instead of papering over it - silently accepting stale
            // data here was part of what caused the login redirect loop.
            if (isUnauthorizedError(error)) {
              throw error;
            }

            console.error(
              'Failed to fetch current user from API; falling back to cached/MSAL user:',
              error,
            );

            // If stored data belongs to the same username, prefer it because it
            // may include app-derived fields not present in bare MSAL profile.
            if (storedUser && storedUser.username === appUser.username) {
              return {
                ...storedUser,
                imageUrl: photoUrl ?? storedUser.imageUrl,
              };
            }

            // Last-resort fallback ensures user can still enter the app with
            // identity data derived from MSAL, even when API is unavailable.
            return {
              ...appUser,
              imageUrl: photoUrl ?? appUser.imageUrl,
            };
          }
        };

        // Wraps `resolveCurrentUser`, additionally treating a rethrown 401 as
        // proof the cached session is invalid: clears local state and forces a
        // clean re-auth instead of letting the caller mark the user
        // "authenticated" with fallback data. Returns `null` once it has
        // already handled the redirect, so the caller can bail out.
        const resolveCurrentUserOrForceReauth = async (
          appUser: IUser,
          photoUrl: string | null,
        ): Promise<IUser | null> => {
          try {
            return await resolveCurrentUser(appUser, photoUrl);
          } catch (error) {
            if (isUnauthorizedError(error)) {
              clearSessionAndRedirectToLogin(
                '🔍 Backend rejected the current session (401), clearing stale session and redirecting to login...',
              );
              return null;
            }
            throw error;
          }
        };

        // Initialize MSAL and handle redirect
        const authResult = await initializeMsal();
        consoleLog.log('📋 MSAL authResult:', authResult);

        // Handle redirect response if user just completed authentication
        if (authResult && authResult.account) {
          // Fetch user photo from Microsoft Graph
          const photoUrl = await fetchGraphUserPhoto();

          consoleLog.group('✅ Using MSAL authentication flow');
          consoleLog.log('🔍 About to fetch Graph user photo...');
          consoleLog.log('📸 photoUrl result:', photoUrl);
          consoleLog.groupEnd();

          const appUser = mapAzureUserToAppUser(authResult.account, undefined, photoUrl);

          // Call current-user API when available, but do not fail auth on transient API errors.
          const currentUser = await resolveCurrentUserOrForceReauth(appUser, photoUrl);
          if (!currentUser) {
            return;
          }
          consoleLog.log('📋 Current user data:', currentUser);

          setAuthenticatedUser(currentUser);
          setIsLoading(false);
          return;
        }

        // No redirect result. Try to recover active account from MSAL cache.
        // MSAL's account cache can lag briefly right after a redirect/navigation
        // (or a version-guard reload), so we re-check a couple of times with a
        // short delay before concluding the user is unauthenticated. MSAL reads
        // localStorage on each lookup, so a delayed re-check surfaces an account
        // that was just committed by the callback flow or another tab. This
        // prevents a transient empty read from wiping a valid session and
        // bouncing the user to login (a login-loop symptom).
        const ACCOUNT_RETRY_ATTEMPTS = 2;
        const ACCOUNT_RETRY_DELAY_MS = 100;

        const resolveActiveAccountWithRetry = async () => {
          let account = getActiveAccount();

          for (let attempt = 0; !account && attempt < ACCOUNT_RETRY_ATTEMPTS; attempt += 1) {
            await new Promise((resolve) => {
              window.setTimeout(resolve, ACCOUNT_RETRY_DELAY_MS);
            });
            account = getActiveAccount();
          }

          return account;
        };

        // Check for MSAL authenticated account
        consoleLog.log('🔍 Checking for existing MSAL account...');
        const msalAccount = await resolveActiveAccountWithRetry();
        consoleLog.log('📋 MSAL account found:', msalAccount);

        if (msalAccount) {
          // A cached account can outlive its usable session (expired refresh
          // token, revoked/expired AAD session, etc.) - MSAL still returns it
          // from localStorage even though no token can ever be issued for it
          // again. Verify the session is actually usable BEFORE trusting it;
          // treating "an account object exists" as "authenticated" without
          // this check is what previously caused an infinite login redirect
          // loop (the same dead account was retried on every reload without
          // ever being cleared).
          const sessionIsValid = await isAccountSessionValid();

          if (!sessionIsValid) {
            if (!pathname.startsWith('/auth')) {
              trackAuthenticationFailure({
                stage: 'session_restore',
                reasonCode: 'cached_session_invalid',
                flushBeforeNavigation: true,
              });
            }

            clearSessionAndRedirectToLogin(
              '🔍 Cached MSAL account has no usable session, clearing stale session and redirecting to login...',
            );
            return;
          }

          // Fetch user photo from Microsoft Graph
          const photoUrl = await fetchGraphUserPhoto();

          consoleLog.group('✅ Using existing MSAL authentication');
          consoleLog.log('🔍 About to fetch Graph user photo for existing account...');
          consoleLog.log('📸 photoUrl result:', photoUrl);
          consoleLog.groupEnd();

          // User is authenticated via MSAL, map to app user
          const appUser = mapAzureUserToAppUser(msalAccount, undefined, photoUrl);

          // Call current-user API when available, but do not fail auth on transient API errors.
          const currentUser = await resolveCurrentUserOrForceReauth(appUser, photoUrl);
          if (!currentUser) {
            return;
          }
          consoleLog.log('📋 Current user data:', currentUser);

          setAuthenticatedUser(currentUser);
          setIsLoading(false);
          return;
        }

        // No MSAL identity available. There is no account/token in MSAL's
        // cache to clean up here (unlike the "invalid cached account" case
        // above), so this only needs to clear the app's own local flags and
        // softly route to login - no need for the heavier `/logout` hard
        // reload. Using that unconditionally here previously caused a
        // *second* infinite loop: landing on `/auth/login` (which has no
        // account either, since the user hasn't signed in yet) forced a
        // `/logout` round-trip back to itself, forever.
        consoleLog.log(
          '🔍 No MSAL account found, clearing stale session and redirecting to login...',
        );
        localStorage.removeItem('user');
        localStorage.removeItem('authenticated');
        router.replace(ROUTES.LOGIN);
      } catch (error) {
        // Bootstrap should fail soft with a user-visible notification.
        // This avoids white-screen behavior and provides feedback.
        trackAuthenticationFailure({
          stage: 'bootstrap',
          reasonCode: 'bootstrap_failed',
          unexpectedError: error,
        });
        addNotification({
          id: `auth-error-${Date.now()}`,
          message: 'An error occurred during authentication. Please try again.',
          variant: 'error',
          createdAt: Date.now(),
          isRead: false,
        });
        console.error('Error checking authentication:', error);
      } finally {
        // Always release loading/lock flags so the app can proceed or retry.
        setIsLoading(false);
        isInitializing.current = false; // Allow future auth checks
      }
    };

    checkAuth();
  }, [setCurrentUser, pathname, addNotification]);

  /**
   * Handle route protection based on authentication and role.
   * Redirects users to appropriate pages based on their auth status and role.
   * Runs on pathname changes.
   */
  const handleRouteProtection = useCallback(() => {
    // Route category flags drive redirect behavior below.
    const isAuthRoute = pathname.startsWith('/auth');
    const isProtectedRoute = pathname.startsWith('/sales-rep') || pathname.startsWith('/manager');

    // If on root path
    if (pathname === '/') {
      if (user) {
        // Authenticated users should never stay on root. Route them by role.
        // User is authenticated, redirect to appropriate dashboard
        switch (user.role) {
          case 'rep':
            return router.replace(ROUTES.SALES_REP_DASHBOARD);
          default:
            return router.replace(ROUTES.MANAGER_DASHBOARD);
        }
      } else {
        // Anonymous root access always routes to login.
        // User is not authenticated, redirect to login
        router.replace(ROUTES.LOGIN);
      }
      return;
    }

    // If on auth route and already authenticated, redirect to appropriate dashboard
    if (isAuthRoute && user) {
      // Logged-in users visiting auth screens are redirected to dashboard.
      switch (user.role) {
        case 'rep':
          return router.replace(ROUTES.SALES_REP_DASHBOARD);
        default:
          return router.replace(ROUTES.MANAGER_DASHBOARD);
      }
    }

    // If on protected route but not authenticated, redirect to login
    if (isProtectedRoute && !user) {
      if (typeof window !== 'undefined') {
        // Save exact path+query so login flow can restore user intent.
        const redirectTarget = `${window.location.pathname}${window.location.search}`;
        window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, redirectTarget);
      }

      const loginUrl = `${ROUTES.LOGIN}?redirect=${encodeURIComponent(pathname)}`;
      router.replace(loginUrl);
      return;
    }
  }, [pathname, user, router]);

  /**
   * Handle route protection after auth check completes
   */
  useEffect(() => {
    // Route protection runs only once bootstrap has settled to avoid false
    // redirects while user state is still being resolved.
    if (!isLoading) handleRouteProtection();
  }, [isLoading, handleRouteProtection]);

  /**
   * Login function.
   *
   * Sets user state and local storage, redirects to appropriate dashboard.
   * @param userData - The user data to set on login
   */
  const login = useCallback(
    (userData: IUser, redirectTo?: string) => {
      // Keep quick auth telemetry for local debugging visibility.
      quickLog.auth('Login function called for user:', userData.email);

      // Mirror authenticated user into local component state + global store +
      // localStorage for compatibility with legacy recovery paths.
      setCurrentUser(userData);
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('authenticated', 'true');

      // Defensive redirect validation prevents open-redirect style behavior.
      // We only allow in-app absolute paths that are not auth routes.
      const isSafeAppPath =
        !!redirectTo &&
        redirectTo.startsWith('/') &&
        !redirectTo.startsWith('/auth') &&
        !redirectTo.startsWith('//');

      if (isSafeAppPath) {
        // Clear stale redirect markers once a trusted redirect is used.
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        }
        router.replace(redirectTo);
        return;
      }

      // Redirect to appropriate dashboard
      if (userData.role === 'rep') {
        router.push(ROUTES.SALES_REP_DASHBOARD);
      } else {
        // Manager dashboard routing is feature-flagged to allow staged rollout.
        if (FEATURE_FLAGS.ENABLE_MANAGER_DASHBOARD) {
          router.push(ROUTES.MANAGER_DASHBOARD);
        }
      }
    },
    [setCurrentUser, router],
  );

  /**
   * Logout function.
   *
   * Clears user state and local storage, handles MSAL sign-out if applicable.
   * Redirects to login page after logout.
   */
  const logout = useCallback(async () => {
    // Logout aggressively clears browser-scoped identity artifacts so a future
    // user session starts from a clean slate.
    // Clear user state and local storage
    setUser(null);
    localStorage.clear();
    sessionStorage.clear();

    // Clear Graph API cache
    const { clearGraphCache } = await import('@/lib/msal');
    clearGraphCache();

    // Check if user was authenticated via MSAL
    const msalAccount = getActiveAccount();
    if (msalAccount) {
      // Federated sign-out when MSAL session exists.
      // Sign out from MSAL and redirect to Azure AD logout
      await msalSignOut();
    } else {
      // Local-only session fallback.
      // Use a server redirect so the response can clear browser data.
      window.location.assign(ROUTES.LOGOUT);
    }
  }, [router]);

  /**
   * Auth context value
   */
  const value = {
    // Context consumers read these values to render auth-aware UI and actions.
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </MsalProvider>
  );
}
